import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Button, Chip } from '@mui/material';
import { TrendingUp, WarningAmber, CheckCircle, RadioButtonUnchecked, Autorenew } from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';

const OrderFulfillmentControl: React.FC = () => {
  const navigate = useNavigate();
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { financeWorkspaceService.salesControl().then(setD).catch(() => setD(null)).finally(() => setLoading(false)); }, []);
  const m = d?.metrics || {};
  const C = useControlRoomColors();

  const stageDot = (s: string) => {
    if (s === 'done') return <CheckCircle sx={{ fontSize: 16, color: C.good }} />;
    if (s === 'active') return <Autorenew sx={{ fontSize: 16, color: C.accent }} />;
    return <RadioButtonUnchecked sx={{ fontSize: 16, color: C.sub }} />;
  };

  const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string; sub?: string }> = ({ label, value, color, sub }) => (
    <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
      <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 24, color: color || C.text }}>{value}</Typography>
      {sub && <Typography sx={{ color: C.sub, fontSize: 11 }}>{sub}</Typography>}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: `linear-gradient(90deg, ${C.panel}, ${C.bg})` }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TrendingUp sx={{ color: C.good }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Order Fulfillment Control</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>Can we fulfil customer demand?</Typography>
          </Box>
        </Stack>
        <Button variant="outlined" onClick={() => navigate('/sales/orders')} sx={{ color: C.text, borderColor: C.line }}>All Sales Orders</Button>
      </Box>
      {loading && <LinearProgress />}

      <Box sx={{ px: 3, py: 2.5, display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(5,1fr)' }, gap: 1.5 }}>
        <Metric label="Active Orders" value={m.activeOrders ?? '—'} sub={m.openValue != null ? formatIndianNumber(m.openValue) : ''} />
        <Metric label="At Risk" value={m.atRisk ?? '—'} color={m.atRisk > 0 ? C.bad : C.text} sub="delivery overdue" />
        <Metric label="Ready to Dispatch" value={m.readyToDispatch ?? '—'} color={C.accent} />
        <Metric label="Revenue (FY)" value={m.revenue != null ? formatIndianNumber(m.revenue) : '—'} color={C.good} />
        <Metric label="Fulfilment" value={d ? `${Math.round((d.orders.reduce((s: number, o: any) => s + o.progress, 0) / Math.max(d.orders.length, 1)))}%` : '—'} color={C.warn} sub="avg progress" />
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>
        <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>Order Fulfillment · click to open</Typography>
        <Stack spacing={1.25}>
          {(d?.orders || []).map((o: any) => (
            <Box key={o.id} onClick={() => navigate('/sales/orders')} sx={{ cursor: 'pointer', bgcolor: C.panel, border: `1px solid ${o.atRisk ? C.bad : C.line}`, borderRadius: 2, p: 2, '&:hover': { borderColor: C.accent } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5}>
                <Box sx={{ minWidth: 220 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{o.orderNumber}</Typography>
                    {o.atRisk && <Chip size="small" icon={<WarningAmber />} label="At risk" sx={{ bgcolor: 'transparent', color: C.bad, border: `1px solid ${C.bad}`, fontSize: 10 }} />}
                  </Stack>
                  <Typography sx={{ fontSize: 13, color: C.sub }} noWrap>{o.customer}</Typography>
                  <Typography sx={{ fontSize: 14, color: C.good, fontWeight: 700 }}>{formatIndianNumber(o.amount)}</Typography>
                </Box>
                {/* Journey strip */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', overflowX: 'auto' }}>
                  {o.journey.map((j: any, i: number) => (
                    <React.Fragment key={j.k}>
                      <Stack alignItems="center" spacing={0.25} sx={{ minWidth: 74 }}>
                        {stageDot(j.s)}
                        <Typography sx={{ fontSize: 10, color: j.s === 'done' ? C.text : C.sub }}>{j.k}</Typography>
                      </Stack>
                      {i < o.journey.length - 1 && <Box sx={{ flex: 1, height: 2, minWidth: 12, bgcolor: j.s === 'done' ? C.good : C.line }} />}
                    </React.Fragment>
                  ))}
                </Box>
                <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, fontFamily: 'monospace', color: C.text }}>{o.progress}%</Typography>
                </Box>
              </Stack>
            </Box>
          ))}
          {!loading && !(d?.orders || []).length && <Typography sx={{ color: C.sub }}>No active orders.</Typography>}
        </Stack>
      </Box>
    </Box>
  );
};

export default OrderFulfillmentControl;
